import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import MapIcon from '@mui/icons-material/Map';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Card, CardContent, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/joy';

// Mock data for shortcuts
const mockShortcuts = [
  {
    id: 'shortcut-001',
    name: 'New Campaign',
    icon: 'add',
    path: '/campaigns/new',
    color: 'primary'
  },
  {
    id: 'shortcut-002',
    name: 'Drone Fleet',
    icon: 'drone',
    path: '/drones',
    color: 'neutral'
  },
  {
    id: 'shortcut-003',
    name: 'Map View',
    icon: 'map',
    path: '/map-drone-view',
    color: 'success'
  },
  {
    id: 'shortcut-004',
    name: 'Image Gallery',
    icon: 'camera',
    path: '/images',
    color: 'warning'
  },
  {
    id: 'shortcut-005',
    name: 'Reports',
    icon: 'reports',
    path: '/reports',
    color: 'info'
  },
  {
    id: 'shortcut-006',
    name: 'Settings',
    icon: 'settings',
    path: '/account-preferences',
    color: 'neutral'
  }
];

interface QuickAccessShortcutsProps {
  shortcuts?: typeof mockShortcuts;
}

const QuickAccessShortcuts = ({ shortcuts = mockShortcuts }: QuickAccessShortcutsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Function to get the appropriate icon for each shortcut
  const getShortcutIcon = (iconName: string) => {
    switch (iconName) {
      case 'add':
        return <AddIcon />;
      case 'drone':
        return <AirplanemodeActiveIcon />;
      case 'map':
        return <MapIcon />;
      case 'camera':
        return <CameraAltIcon />;
      case 'reports':
        return <AssessmentIcon />;
      case 'settings':
        return <SettingsIcon />;
      default:
        return <AddIcon />;
    }
  };

  // Function to handle shortcut click
  const handleShortcutClick = (path: string) => {
    navigate(path);
  };

  return (
    <Card variant="outlined">
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography level="title-md">{t('dashboard.quick_access', 'Quick Access Shortcuts')}</Typography>
      </Box>
      <CardContent>
        <Grid container spacing={2}>
          {shortcuts.map((shortcut) => (
            <Grid xs={4} sm={2} key={shortcut.id}>
              <Stack alignItems="center" spacing={1}>
                <Tooltip title={t(`dashboard.shortcut_${shortcut.name.toLowerCase().replace(' ', '_')}`, shortcut.name)}>
                  <IconButton
                    variant="soft"
                    color={shortcut.color as any}
                    size="lg"
                    onClick={() => handleShortcutClick(shortcut.path)}
                    sx={{ borderRadius: '50%' }}
                  >
                    {getShortcutIcon(shortcut.icon)}
                  </IconButton>
                </Tooltip>
                <Typography level="body-xs" textAlign="center">
                  {t(`dashboard.shortcut_${shortcut.name.toLowerCase().replace(' ', '_')}`, shortcut.name)}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default QuickAccessShortcuts;
